import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Rabbit, ArrowRight, RotateCcw, Sparkles, Loader2, Send, ChevronRight, Bookmark, Check, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";
import { playDive, unlockAudio } from "@/lib/sound";
import { useAuth } from "@/context/AuthContext";

const INTRO_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/rnunsk9p_gemini_generated_video_e7347223.mp4";

const T = {
  fr: { eyebrow: "De curiosité en curiosité", title: "Le Terrier du Lapin", intro: "Pose une question, ou laisse-toi guider. Chaque réponse ouvre trois nouvelles portes. Jusqu'où iras-tu ?", start: "Descends dans le terrier", surprise: "Surprends-moi", placeholder: "Ta curiosité du moment…", restart: "Recommencer", depth: "Profondeur", error: "Le terrier s'est refermé. Réessaie.", loading: "On creuse…", save: "Sauvegarder ce chemin", saved: "Chemin sauvegardé", saving: "Sauvegarde…", loginToSave: "Connecte-toi pour sauvegarder ton chemin.", saveError: "Impossible de sauvegarder. Réessaie.", soundOn: "Couper le son", soundOff: "Activer le son" },
  en: { eyebrow: "From curiosity to curiosity", title: "The Rabbit Hole", intro: "Ask a question, or let yourself be guided. Every answer opens three new doors. How far will you go?", start: "Dive into the rabbit hole", surprise: "Surprise me", placeholder: "Your curiosity right now…", restart: "Start over", depth: "Depth", error: "The hole closed up. Try again.", loading: "Digging…", save: "Save this trail", saved: "Trail saved", saving: "Saving…", loginToSave: "Log in to save your trail.", saveError: "Couldn't save. Try again.", soundOn: "Mute", soundOff: "Unmute" },
  es: { eyebrow: "De curiosidad en curiosidad", title: "La Madriguera", intro: "Haz una pregunta o déjate guiar. Cada respuesta abre tres puertas nuevas. ¿Hasta dónde llegarás?", start: "Baja a la madriguera", surprise: "Sorpréndeme", placeholder: "Tu curiosidad de ahora…", restart: "Empezar de nuevo", depth: "Profundidad", error: "La madriguera se cerró. Inténtalo de nuevo.", loading: "Cavando…", save: "Guardar este camino", saved: "Camino guardado", saving: "Guardando…", loginToSave: "Inicia sesión para guardar tu camino.", saveError: "No se pudo guardar. Inténtalo de nuevo.", soundOn: "Silenciar", soundOff: "Activar sonido" },
  nl: { eyebrow: "Van nieuwsgierigheid naar nieuwsgierigheid", title: "Het Konijnenhol", intro: "Stel een vraag of laat je leiden. Elk antwoord opent drie nieuwe deuren. Hoe ver ga jij?", start: "Duik in het konijnenhol", surprise: "Verras me", placeholder: "Jouw nieuwsgierigheid nu…", restart: "Opnieuw", depth: "Diepte", error: "Het hol sloot zich. Probeer opnieuw.", loading: "Graven…", save: "Bewaar dit pad", saved: "Pad bewaard", saving: "Bewaren…", loginToSave: "Log in om je pad te bewaren.", saveError: "Kon niet bewaren. Probeer opnieuw.", soundOn: "Dempen", soundOff: "Geluid aan" },
};

export const Terrier = () => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const lang = (i18n.language || "fr").slice(0, 2);
  const c = T[lang] || T.fr;
  const [node, setNode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [path, setPath] = useState([]);
  const [input, setInput] = useState("");
  const [soundOn, setSoundOn] = useState(true);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved

  const dive = async (topic, pushTitle) => {
    setLoading(true);
    setSaveState("idle");
    if (soundOn) { unlockAudio(); playDive(); }
    try {
      const { data } = await api.get("/rabbithole", { params: { lang, topic: topic || "" } });
      if (pushTitle) setPath((p) => [...p, pushTitle]);
      setNode(data);
    } catch {
      toast.error(c.error);
    } finally {
      setLoading(false);
    }
  };

  const start = (topic) => { if (soundOn) unlockAudio(); setPath([]); setNode(null); setSaveState("idle"); dive(topic || "", null); };
  const startTopic = () => { const v = input.trim(); if (!v) return; setInput(""); start(v); };
  const restart = () => { setNode(null); setPath([]); setInput(""); setSaveState("idle"); };
  const depth = node ? path.length + 1 : 0;

  const savePath = async () => {
    if (!node) return;
    if (!user) { toast.info(c.loginToSave); return; }
    const steps = [...path, node.title].filter(Boolean);
    if (steps.length === 0) return;
    setSaveState("saving");
    try {
      await api.post("/rabbithole/path", { steps });
      setSaveState("saved");
      toast.success(c.saved);
    } catch {
      setSaveState("idle");
      toast.error(c.saveError);
    }
  };

  return (
    <section className="relative px-6 my-20" data-testid="terrier-section">
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-[2rem] overflow-hidden border border-primary/20 frame-pulse glass">
          <div aria-hidden className="absolute inset-0 pointer-events-none">
            <div className="absolute left-1/2 top-24 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-primary/10 blur-[120px]" />
            {[...Array(6)].map((_, i) => (
              <motion.span key={i} className="absolute w-1.5 h-1.5 rounded-full bg-primary/40"
                style={{ left: `${12 + i * 14}%`, top: `${20 + (i % 3) * 22}%` }}
                animate={{ y: [0, -18, 0], opacity: [0.2, 0.7, 0.2] }}
                transition={{ duration: 4 + i, repeat: Infinity, delay: i * 0.5 }} />
            ))}
          </div>

          <div className="relative z-10 px-6 sm:px-12 py-14">
            <div className="max-w-3xl mx-auto text-center">
        <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-4">
          <Rabbit size={14} /> {c.eyebrow}
        </p>
        <h2 className="font-display text-4xl sm:text-6xl text-gradient-orange leading-[1.12] pb-[0.08em]">{c.title}</h2>
        <p className="mt-4 text-base text-foreground/60 max-w-xl mx-auto">{c.intro}</p>

        <div className="mt-9 max-w-2xl mx-auto">
          <CinematicVideo fade noSound silver src={INTRO_VIDEO} testId="terrier-cinematic-video" />
        </div>

        {!node && !loading && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-9">
            <div className="flex flex-col sm:flex-row items-center gap-3 max-w-lg mx-auto">
              <input
                data-testid="terrier-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && startTopic()} placeholder={c.placeholder}
                className="w-full rounded-full bg-white/[0.04] border border-white/12 focus:border-primary/50 outline-none px-5 py-3.5 text-sm text-foreground/90 transition-colors"
              />
              <button onClick={startTopic} data-testid="terrier-explore" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm whitespace-nowrap">
                <Send size={15} />
              </button>
            </div>
            <div className="mt-5 flex items-center justify-center gap-5">
              <button onClick={() => start("")} data-testid="terrier-surprise" className="inline-flex items-center gap-2 text-primary/85 hover:text-primary text-sm transition-colors">
                <Sparkles size={15} /> {c.surprise}
              </button>
              <button
                onClick={() => setSoundOn((s) => !s)}
                data-testid="terrier-sound-toggle"
                aria-label={soundOn ? c.soundOn : c.soundOff}
                title={soundOn ? c.soundOn : c.soundOff}
                className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/12 text-foreground/55 hover:text-primary hover:border-primary/40 transition-colors"
              >
                {soundOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
              </button>
            </div>
          </motion.div>
        )}
      </div>

      {loading && (
        <div className="relative z-10 mt-10 flex flex-col items-center gap-3 text-foreground/60" data-testid="terrier-loading">
          <Loader2 size={26} className="animate-spin text-primary" />
          <span className="text-sm">{c.loading}</span>
        </div>
      )}

      <AnimatePresence mode="wait">
        {node && !loading && (
          <motion.div
            key={node.title}
            initial={{ opacity: 0, y: 26, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -20, filter: "blur(6px)" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 mt-10 max-w-2xl mx-auto"
            data-testid="terrier-node"
          >
            <div className="flex items-center justify-between mb-4 text-xs">
              <span className="inline-flex items-center gap-1.5 text-primary/70">
                <Rabbit size={13} /> {c.depth} {depth}
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSoundOn((s) => !s)}
                  data-testid="terrier-sound-toggle-node"
                  aria-label={soundOn ? c.soundOn : c.soundOff}
                  title={soundOn ? c.soundOn : c.soundOff}
                  className="inline-flex items-center gap-1.5 text-foreground/45 hover:text-primary transition-colors"
                >
                  {soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
                </button>
                <button onClick={restart} data-testid="terrier-restart" className="inline-flex items-center gap-1.5 text-foreground/45 hover:text-primary transition-colors">
                  <RotateCcw size={13} /> {c.restart}
                </button>
              </div>
            </div>

            {path.length > 0 && (
              <p className="text-[11px] text-foreground/35 mb-4 flex items-center gap-1 flex-wrap" data-testid="terrier-path">
                {path.slice(-3).map((p, i) => (
                  <span key={i} className="inline-flex items-center gap-1">{i > 0 && <ChevronRight size={10} />}{p}</span>
                ))}
                <ChevronRight size={10} /> <span className="text-primary/60">{node.title}</span>
              </p>
            )}

            <div className="rounded-3xl border border-primary/25 bg-white/[0.035] backdrop-blur-xl p-7 sm:p-9 shadow-2xl">
              <h3 className="font-display text-2xl sm:text-3xl text-gradient-orange leading-snug" data-testid="terrier-title">{node.title}</h3>
              <p className="mt-4 text-foreground/80 leading-relaxed text-[15px]" data-testid="terrier-answer">{node.answer}</p>

              {node.rebounds?.length > 0 && (
                <div className="mt-7 grid gap-3">
                  {node.rebounds.map((r, i) => (
                    <motion.button
                      key={i}
                      initial={{ opacity: 0, x: -16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.25 + i * 0.12 }}
                      onClick={() => dive(r.topic, node.title)}
                      data-testid={`terrier-rebound-${i}`}
                      className="group flex items-center justify-between gap-3 text-left rounded-2xl border border-white/10 hover:border-primary/45 bg-white/[0.03] hover:bg-primary/[0.06] px-5 py-4 transition-all"
                    >
                      <span className="text-sm text-foreground/85 group-hover:text-primary transition-colors">{r.label}</span>
                      <ArrowRight size={16} className="text-primary/50 group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </motion.button>
                  ))}
                </div>
              )}

              <div className="mt-7 flex justify-center">
                <button
                  onClick={savePath}
                  disabled={saveState !== "idle"}
                  data-testid="terrier-save-path"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary/30 text-primary/85 hover:text-primary hover:border-primary/60 hover:bg-primary/[0.06] text-sm transition-all disabled:opacity-60"
                >
                  {saveState === "saving" ? <Loader2 size={15} className="animate-spin" /> : saveState === "saved" ? <Check size={15} /> : <Bookmark size={15} />}
                  {saveState === "saving" ? c.saving : saveState === "saved" ? c.saved : c.save}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
